import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, Card, Gallery, Heading, Span } from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { FetchingError } from "components/common/fetching-error";
import { Stack } from "components/common/stack";
import { FaqSidebar } from "components/shared/faq-sidebar";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { ThumbtackModalProvider } from "components/thumbtack-modal";
import {
  checkForNaStringValue,
  downloadImage,
  formatDate,
  hideSupportChat,
  showSupportChat,
} from "utils";

import { useInspectionsData } from "./hooks";
import galleryMediaTileStyles from "./inspection-gallery-media-tiles.module.scss";
import { type MediaTileItem } from "./inspection-gallery-types";
import { InspectionsIneligibleOld } from "./inspections-ineligible-old";
import styles from "./inspections-old.module.scss";
import { RoofMarketplaceDrawer } from "./roof-marketplace-drawer";
import { useInspectionsRoofMarketplace } from "./use-inspections-roof-marketplace";
import { WriteOutEntry } from "./write-out-entry";

export type GalleryData = {
  assetIds: string[];
  title: string;
  subHeading: string;
};

const InspectionsOldContent = () => {
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);
  const navigate = useNavigate();
  const {
    error,
    inspectionDueDate,
    latestInspection,
    isLoading: loading,
  } = useInspectionsData();
  const {
    roofMarketplaceWorkflowEnabled,
    roofMarketplaceDataEligible,
    roofMarketplaceCanService,
    openRoofMarketplaceOrThumbtack,
    isRoofMarketplaceDrawerOpen,
    closeRoofMarketplaceDrawer,
    hasExistingBid,
    requestRoofMarketplaceBids,
  } = useInspectionsRoofMarketplace({ latestInspection });
  const referralList = latestInspection?.review?.referrals;
  const sortedReferralList = referralList?.toSorted((referral) => {
    return referral?.category === "Roof" ? -1 : 1;
  });
  const inspectionAssets = latestInspection?.review?.inspectionReviewAssets;
  const statusEnum = latestInspection?.status;
  const isIneligible = statusEnum === 480;
  const noWriteOuts = statusEnum === 200 || statusEnum === 1000;
  const shownAssets = galleryData?.assetIds;
  const showGallery = shownAssets && shownAssets.length > 0;

  useEffect(() => {
    if (showGallery) {
      hideSupportChat();
    }
    return () => showSupportChat();
  }, [showGallery]);

  useEffect(() => {
    if (noWriteOuts) {
      navigate("/my/overview");
    }
  }, [navigate, noWriteOuts]);

  if (error) {
    return (
      <BaseLayout>
        <MainLayout sidebar={<FaqSidebar page="overview" />}>
          <FetchingError page="Inspections" />
        </MainLayout>
      </BaseLayout>
    );
  }

  if (loading) {
    return <BaseLayout loading />;
  }

  if (isIneligible) {
    return <InspectionsIneligibleOld />;
  }

  if (showGallery) {
    const media: MediaTileItem[] = shownAssets.map((assetId) => {
      const selectedAsset = inspectionAssets?.find(
        (asset) => asset.id === assetId,
      );
      const fileName = selectedAsset?.assetFileName;
      return {
        fileName: fileName ?? `image_${assetId}`,
        id: assetId,
        source: `/api/inspection/image/${assetId}`,
      };
    });

    return (
      <Gallery
        className={galleryMediaTileStyles.galleryRoot}
        media={media}
        activeIndex={3}
        title={galleryData.title}
        subheading={galleryData.subHeading}
        primaryAction="Close"
        onPrimaryAction={() => setGalleryData(null)}
        onClose={() => setGalleryData(null)}
        activeAction="Download"
        showHeroFooter={true}
        onActiveAction={(active: MediaTileItem) => {
          if (active?.source) {
            downloadImage(active.source, active.fileName);
          }
        }}
      />
    );
  }

  return (
    <>
      <BaseLayout>
        <MainLayout
          topRow={
            <Stack gap="md" className={styles.topRow}>
              <Heading size="lg">Our inspection findings</Heading>
              <Alert
                appearance="danger"
                description={
                  <>
                    Please make these fixes{" "}
                    {inspectionDueDate ? (
                      <Span>
                        by <Span bold>{formatDate(inspectionDueDate)} </Span>
                      </Span>
                    ) : null}
                    and send proof to your insurance rep. We&apos;ll take a look
                    and confirm everything within a week.
                  </>
                }
                title=""
              />
            </Stack>
          }
          sidebar={<InsuranceRepresentative />}
        >
          <Card className={styles.headingCardDS} border>
            {sortedReferralList?.length ? (
              <>
                <Heading size="md">
                  {`Action needed on ${sortedReferralList.length} item${
                    sortedReferralList.length > 1 ? "s" : ""
                  }`}
                </Heading>
                <Stack gap="xl" className={styles.writeOuts}>
                  {sortedReferralList.map((referral, idx) => {
                    const galleryTitle =
                      checkForNaStringValue(referral?.issueStatement ?? "") ??
                      "";
                    const gallerySubHeading =
                      checkForNaStringValue(referral?.locationDetail ?? "") ??
                      "";
                    return (
                      <WriteOutEntry
                        referral={referral ?? {}}
                        showGallery={() => {
                          if (referral?.inspectionReviewAssetIds) {
                            setGalleryData({
                              assetIds: referral.inspectionReviewAssetIds,
                              title: galleryTitle,
                              subHeading: gallerySubHeading,
                            });
                          }
                        }}
                        roofMarketplaceEligible={roofMarketplaceDataEligible}
                        roofMarketplaceDrawerAvailable={
                          roofMarketplaceCanService === true
                        }
                        onOpenRoofMarketplace={
                          roofMarketplaceWorkflowEnabled
                            ? openRoofMarketplaceOrThumbtack
                            : undefined
                        }
                        key={idx}
                      />
                    );
                  })}
                </Stack>
              </>
            ) : null}
          </Card>
        </MainLayout>
      </BaseLayout>
      {roofMarketplaceWorkflowEnabled ? (
        <RoofMarketplaceDrawer
          isOpen={isRoofMarketplaceDrawerOpen}
          onClose={closeRoofMarketplaceDrawer}
          hasExistingBid={hasExistingBid}
          onRequestBids={requestRoofMarketplaceBids}
        />
      ) : null}
    </>
  );
};

/**
 * `InspectionsOldContent` reads `ThumbtackModalContext` (via `useInspectionsRoofMarketplace`),
 * so the provider has to live in an ancestor — a component cannot consume context from a
 * provider it renders itself. See PPIX-2726.
 */
export const InspectionsOld = () => (
  <ThumbtackModalProvider>
    <InspectionsOldContent />
  </ThumbtackModalProvider>
);
